<!-- UserOrderHistory.vue -->
<template>
  <div class="user-order-history-container">
    <div class="header">
      <h1>My Orders</h1>
    </div>
    
    
    <!-- Search Section -->
    <div class="search-section">
      <div class="search-container">
        <div class="search-input-group">
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Search by pharmacy, order number, or date..."
            class="search-input"
            @input="handleSearch"
          />
          <button @click="clearSearch" class="clear-search-btn" v-if="searchQuery">
            ✕
          </button>
        </div>
      </div>
    </div>
    
    <!-- Filter Section -->
    <div class="filter-section">
      <div class="filter-container">
        <div class="filter-tabs">
          <button 
            v-for="status in filterStatuses" 
            :key="status.value"
            @click="setStatusFilter(status.value)"
            class="filter-tab"
            :class="{ active: currentStatusFilter === status.value }"
          >
            {{ status.label }}
            <span class="count-badge">{{ getStatusCount(status.value) }}</span>
          </button>
        </div>
      </div>
    </div>


    
    
    <div class="orders-content">
      <div v-if="loading" class="loading">
        Loading orders...
      </div>
      
      <div v-else-if="filteredOrders.length === 0 && !searchQuery && currentStatusFilter === 'all'" class="no-orders">
        No orders found
      </div>
      
      <div v-else-if="filteredOrders.length === 0" class="no-results">
        No orders match your search criteria
      </div>
      
      <div v-else class="orders-list">
        <div 
          v-for="order in filteredOrders" 
          :key="order.order_id"
          class="order-card"
          :class="{ expanded: expandedOrders.includes(order.order_id) }"
        >
          <!-- Order Summary -->
          <div class="order-summary">
            <div class="order-header">
              <div class="order-info">
                <div class="order-id"> {{ order.order_id }}</div>
                <div class="order-date">{{ formatDate(order.order_date) }}</div>
                <div class="payment-status">Payment status: {{ order.payment_status || 'Pending' }}</div>
              </div>
              <div class="order-totals">
                <div class="total-items">Items: {{ order.total_item_count || order.order_details.length }}</div>
                <div class="total-amount">৳{{ getOrderSummary(order).total.toFixed(2) }}</div>
              </div>
            </div>
            
            <div class="customer-info">
              <div class="mobile">{{ order.customer_name }}</div>
            </div>
            
            <div class="order-meta">
              <div class="payment-method">Payment method: {{ order.payment_method || 'COD' }}</div>
              <div class="order-status" :class="getOrderStatusClass(order.order_status)">
                {{ order.order_status }}
              </div>
            </div>
            
            <button 
              @click="toggleOrderDetails(order.order_id)"
              class="see-details-btn"
            >
              {{ expandedOrders.includes(order.order_id) ? 'Hide Details' : 'See Details' }}
            </button>
          </div>
          
          <!-- Order Details (Expandable) -->
          <div v-if="expandedOrders.includes(order.order_id)" class="order-details">
            <div class="details-header">
              <h3>Order Items</h3>
              <div class="items-summary">
                Total Items: {{ order.order_details.length }} | 
                Amount: ৳{{ getOrderSummary(order).total.toFixed(2) }}
              </div>
            </div>
            
            <div class="items-list">
              <div 
                v-for="(item, index) in order.order_details" 
                :key="`${order.order_id}-${item.item_id}-${index}`"
                class="item-card"
                :class="{ 'acknowledged': isOrderAcknowledged(order.order_status) }"
              >
                <div class="item-info">
                  <div class="medicine-name">{{ item.item_name }}</div>
                  <div class="medicine-category">{{ item.item_category }} {{ item.item_strength }}</div>
                  <div class="item-details">
                    <span class="quantity">Qty: {{ item.qty }} {{ item.item_pack_type }}  ( 1 {{ item.item_pack_type }} : {{ item.item_pack_amount }} {{ item.item_formation }})</span>
                    <span v-if="item.bonus_qty > 0" class="bonus-qty">
                      + {{ item.bonus_qty }} bonus
                    </span>
                    <span class="amount">
                      <span v-if="isOrderAcknowledged(order.order_status) && item.available !== 'yes'">
                        ৳{{ getItemDisplayAmount(item).toFixed(2) }}
                        <small v-if="item.available === 'partially'" class="original-amount">
                          (Original: ৳{{ item.total_amount.toFixed(2) }})
                        </small>
                        <small v-if="item.available === 'no'" class="unavailable-amount">
                          (Unavailable: ৳{{ item.total_amount.toFixed(2) }})
                        </small>
                      </span>
                      <span v-else>
                        ৳{{ item.total_amount.toFixed(2) }}
                      </span>
                    </span>
                  </div>
                </div>
                
                <!-- In the availability-status section -->
                <div class="availability-status">
                  <span 
                    v-if="isOrderAcknowledged(order.order_status)"
                    class="availability-indicator" 
                    :class="getAvailabilityClass(item.available)"
                  >
                    {{ getAvailabilityText(item.available, item.available_qty, item.qty) }}
                    <!-- <small v-if="item.available === 'partially'" class="price-info">
                      ৳{{ getItemDisplayAmount(item).toFixed(2) }}
                    </small> -->
                  </span>
                  <span 
                    v-else
                    class="availability-indicator pending"
                  >
                    Pending Review
                  </span>
                </div>
              </div>
              <div class="note-section" v-if="order.note">
                <h3>Note: {{order.note}}</h3>
              </div>
            </div>

            <!-- Order Summary Section -->
            <div class="order-summary-section">
              <h3>Order Summary</h3>
              <div class="summary-details">
                <div class="summary-row">
                  <span class="summary-label">Total Quantity:</span>
                  <span class="summary-value">{{ getOrderSummary(order).totalQuantity }} quantities</span>
                </div>
                <div class="summary-row">
                  <span class="summary-label">Subtotal:</span>
                  <span class="summary-value">৳{{ getOrderSummary(order).subtotal.toFixed(2) }}</span>
                </div>
                <div class="summary-row" v-if="getOrderSummary(order).discountAmount > 0">
                  <span class="summary-label">Discount:</span>
                  <span class="summary-value discount-amount">
                    -৳{{ getOrderSummary(order).discountAmount.toFixed(2) }}
                  </span>
                </div>
                <div class="summary-row total-row">
                  <span class="summary-label">Total:</span>
                  <span class="summary-value total-amount">৳{{ getOrderSummary(order).total.toFixed(2) }}</span>
                </div>
              </div>
            </div>
            
            <!-- Action Buttons - Only show confirm button for acknowledged orders -->
            <div class="action-buttons" v-if="canShowActionButtons(order.order_status)">
              <button 
                @click="cancelOrder(order.order_id)"
                class="cancel-btn"
                :disabled="cancellingOrder === order.order_id"
              >
                {{ cancellingOrder === order.order_id ? 'Cancelling...' : 'Cancel' }}
              </button>
              
              <!-- Only show confirm button for acknowledged orders -->
              <button 
                v-if="order.order_status === 'acknowledged'"
                @click="confirmOrder(order.order_id)"
                class="confirm-btn"
                :disabled="confirmingOrder === order.order_id"
              >
                {{ confirmingOrder === order.order_id ? 'Confirming...' : 'Confirm' }}
              </button>
            </div>
            
            <!-- Order Status Messages -->
            <div class="status-section">
              <div class="status-message" :class="getOrderStatusClass(order.order_status)">
                {{ getStatusMessage(order.order_status) }} {{ getStatusIcon(order.order_status) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Toast Message -->
    <div v-if="toastMessage" class="toast" :class="toastType">
      {{ toastMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserOrderHistory',
  data() {
    return {
      orders: [],
      filteredOrders: [],
      loading: true,
      expandedOrders: [],
      userName: '',
      userId: '',
      toastMessage: '',
      toastType: '',
      cancellingOrder: null,
      confirmingOrder: null,
      searchQuery: '',
      currentStatusFilter: 'all',
      filterStatuses: [
        { label: 'All', value: 'all' },
        { label: 'Pending', value: 'pending' },
        { label: 'Acknowledged', value: 'acknowledged' },
        { label: 'Confirmed', value: 'confirmed' },
        { label: 'Cancelled', value: 'cancelled' }
      ]
    }
  },
  computed: {
    getOrderSummary() {
      return (order) => {
        // Calculate totals based on available items and quantities
        let totalQuantity = 0
        let subtotal = 0
        
        order.order_details.forEach(item => {
          if (item.available === 'yes') {
            // Full quantity and amount
            totalQuantity += item.qty || 0
            subtotal += item.total_amount || 0
          } else if (item.available === 'partially' && item.available_qty > 0) {
            // Partial quantity and proportional amount
            const originalQty = item.qty || 0
            const availableQty = item.available_qty || 0
            const originalAmount = item.total_amount || 0
            
            if (originalQty > 0) {
              const proportionalAmount = (originalAmount / originalQty) * availableQty
              totalQuantity += availableQty
              subtotal += proportionalAmount
            }
          }
          // If available === 'no', don't add anything to totals
        })
        
        // For user view, use the discount and total from database (already processed by pharmacy)
        const discountAmount = order.total_discount || 0
        const total = order.total_amount || subtotal
        
        return {
          totalQuantity,
          subtotal,
          discountAmount,
          total
        }
      }
    }
  },
  mounted() {
    this.checkLogin()
    this.loadUserOrderHistory()
  },
  methods: {
    getItemDisplayAmount(item) {
      if (item.available === 'yes') {
        return item.total_amount
      } else if (item.available === 'partially' && item.available_qty > 0) {
        const originalQty = item.qty || 0
        const availableQty = item.available_qty || 0
        const originalAmount = item.total_amount || 0
        
        if (originalQty > 0) {
          return (originalAmount / originalQty) * availableQty
        }
      }
      return 0
    },
    checkLogin() {
      const isLoggedIn = localStorage.getItem('isLoggedIn')
      if (!isLoggedIn) {
        this.$router.push('/user_order_history/<user_id>')
        return
      }
      
      this.userName = localStorage.getItem('userName') || 'User'
      this.userId = localStorage.getItem('userId')
    },
    
    async loadUserOrderHistory() {
      try {
        this.loading = true
        // Build query parameters
        const params = new URLSearchParams({
          limit: '50'
        })
        
        if (this.searchQuery) {
          params.append('search', this.searchQuery)
        }
        
        if (this.currentStatusFilter !== 'all') {
          params.append('order_status', this.currentStatusFilter)
        }
        
        // API endpoint with search and filter parameters
        const response = await fetch(`https://e01.yeapps.com/medi360/user_order_history/${this.userId}?${params.toString()}`)
        const data = await response.json()
        
        if (data.order_data && data.order_data[0] && data.order_data[0].status === 'Success') {
          this.orders = data.order_data[0].active_order_list.map(order => {
            return {
              ...order,
              order_details: order.order_details.map(item => {
                return {
                  ...item,
                  // Set availability based on the 'available' field from database
                  available: item.available,
                  available_qty: item.available_qty || 0,
                }
              })
            }
          })
          this.applyFilters()
        } else {
          this.orders = []
          this.filteredOrders = []
          if (data.order_data && data.order_data[0] && data.order_data[0].message) {
            this.showToast(data.order_data[0].message, 'info')
          }
        }
      } catch (error) {
        console.error('Error loading user order history:', error)
        this.showToast('Error loading orders', 'error')
      } finally {
        this.loading = false
      }
    },
    
    applyFilters() {
      let filtered = [...this.orders]
      
      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim()
        filtered = filtered.filter(order => {
          // Search in pharmacy name (customer_name)
          const pharmacyMatch = order.customer_name?.toLowerCase().includes(query)
          
          // Search in order number
          const orderNumberMatch = order.order_id?.toString().toLowerCase().includes(query)
          
          // Search in date
          const dateMatch = this.formatDate(order.order_date).toLowerCase().includes(query)
          
          return pharmacyMatch || orderNumberMatch || dateMatch
        })
      }
      
      // Apply status filter
      if (this.currentStatusFilter !== 'all') {
        filtered = filtered.filter(order => order.order_status === this.currentStatusFilter)
      }
      
      this.filteredOrders = filtered
    },
    
    // New method to get count for each status
    getStatusCount(status) {
      if (status === 'all') {
        return this.orders.length
      }
      
      return this.orders.filter(order => order.order_status === status).length
    },
    
    handleSearch() {
      // Debounce search to avoid too many API calls
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 300)
    },
    
    clearSearch() {
      this.searchQuery = ''
      this.applyFilters()
    },
    
    setStatusFilter(status) {
      this.currentStatusFilter = status
      this.applyFilters()
    },
    
    toggleOrderDetails(orderId) {
      const index = this.expandedOrders.indexOf(orderId)
      if (index > -1) {
        this.expandedOrders.splice(index, 1)
      } else {
        this.expandedOrders.push(orderId)
      }
    },
    
    // Check if order is acknowledged (available for confirmation)
    isOrderAcknowledged(orderStatus) {
      return ['acknowledged', 'confirmed', 'delivered'].includes(orderStatus)
    },
    
    // Check if action buttons should be shown
    canShowActionButtons(orderStatus) {
      return !['cancelled', 'delivered'].includes(orderStatus)
    },

    getAvailabilityClass(availability) {
      switch(availability) {
        case 'yes': return 'available'
        case 'partially': return 'partial'
        case 'no': return 'not-available'
        default: return 'pending'
      }
    },

    getAvailabilityText(availability, availableQty, totalQty) {
      switch(availability) {
        case 'yes': 
          return 'Available'
        case 'partially': 
          return `${availableQty || 0}/${totalQty} Available`
        case 'no': 
          return 'Not Available'
        default: 
          return 'Pending Review'
      }
    },
    
    // Get status message
    getStatusMessage(status) {
      switch(status) {
        case 'pending':
          return 'Order is being reviewed by pharmacy'
        case 'acknowledged':
          return 'Order reviewed - Please confirm to proceed'
        case 'confirmed':
          return 'Order confirmed and being prepared'
        case 'cancelled':
          return 'This order has been cancelled'
        case 'delivered':
          return 'This order has been delivered'
        default:
          return `Order status: ${status}`
      }
    },
    
    // Get status icon
    getStatusIcon(status) {
      switch(status) {
        case 'pending':
          return '⏳'
        case 'acknowledged':
          return '✅'
        case 'confirmed':
          return '📦'
        case 'cancelled':
          return '🚫'
        case 'delivered':
          return '✅'
        default:
          return ''
      }
    },
    
    async cancelOrder(orderId) {
      const order = this.orders.find(o => o.order_id === orderId)
      if (!order) return
      
      if (!confirm('Are you sure you want to cancel this order?')) {
        return
      }
      
      try {
        this.cancellingOrder = orderId
        
        // Prepare data for cancel API
        const requestData = {
          order_id: orderId,
          user_id: this.userId,
          action: 'cancel'
        }
        
        // Send cancel request to API
        const response = await fetch('https://e01.yeapps.com/medi360/update_order_status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        })
        
        const data = await response.json()
        
        if (data.success) {
          this.showToast(data.message || 'Order cancelled successfully', 'success')
          
          // Update the order status locally
          const orderIndex = this.orders.findIndex(o => o.order_id === orderId)
          if (orderIndex !== -1) {
            this.orders[orderIndex].order_status = 'cancelled'
          }
          this.applyFilters()
          
        } else {
          this.showToast(data.error || 'Failed to cancel order', 'error')
        }
        
      } catch (error) {
        console.error('Error cancelling order:', error)
        this.showToast('Network error while cancelling order', 'error')
      } finally {
        this.cancellingOrder = null
      }
    },
    
    async confirmOrder(orderId) {
      const order = this.orders.find(o => o.order_id === orderId)
      if (!order) return
      
      // Only allow confirmation for acknowledged orders
      if (order.order_status !== 'acknowledged') {
        this.showToast('Order must be reviewed by pharmacy before confirmation', 'error')
        return
      }
      
      try {
        this.confirmingOrder = orderId
        
        // Prepare data for confirm API
        const requestData = {
          order_id: orderId,
          user_id: this.userId,
          action: 'confirm'
        }
        
        // Send confirm request to API
        const response = await fetch('https://e01.yeapps.com/medi360/confirm_order', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        })
        
        const data = await response.json()
        
        if (data.success) {
          this.showToast(data.message || 'Order confirmed successfully', 'success')
          
          // Update the order status locally
          const orderIndex = this.orders.findIndex(o => o.order_id === orderId)
          if (orderIndex !== -1) {
            this.orders[orderIndex].order_status = 'confirmed'
          }
          this.applyFilters()
          
        } else {
          this.showToast(data.error || 'Failed to confirm order', 'error')
        }
        
      } catch (error) {
        console.error('Error confirming order:', error)
        this.showToast('Network error while confirming order', 'error')
      } finally {
        this.confirmingOrder = null
      }
    },
    
    getOrderStatusClass(status) {
      switch(status) {
        case 'confirmed':
          return 'status-confirmed'
        case 'cancelled':
          return 'status-cancelled'
        case 'delivered':
          return 'status-delivered'
        case 'pending':
          return 'status-pending'
        case 'acknowledged':
          return 'status-acknowledged'
        default:
          return 'status-default'
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
    },
    
    showToast(message, type) {
      this.toastMessage = message
      this.toastType = type
      setTimeout(() => {
        this.toastMessage = ''
        this.toastType = ''
      }, 3000)
    }
  }
}
</script>

<style scoped>
.original-amount,
.unavailable-amount,
.price-info {
  display: block;
  font-size: 9px; /* Smaller text */
  font-weight: 500;
  color: #bbb7b7;
  margin-top: 1px;
  line-height: 1.0;
}

.unavailable-amount {
  text-decoration: line-through;
  color: #999;
}

.item-card .amount {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
}

.availability-indicator.available {
  background-color: #0a3818;
  color: #51cf66;
  border: 1px solid #51cf66;
}

.availability-indicator.partial {
  background-color: #2d2006;
  color: #ffd43b;
  border: 1px solid #ffd43b;
  font-weight: 600;
}

.availability-indicator.not-available {
  background-color: #2b0e0e;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
}

.availability-indicator.pending {
  background-color: #333;
  color: #f39c12;
  border: 1px solid #f39c12;
}

/* Ensure consistent styling */
.availability-indicator {
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px; /* Slightly smaller for better fit */
  font-weight: 600;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  line-height: 1.2;
}

.user-order-history-container {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background-color: #000;
  color: #e2e2e2;
  max-width: 480px;
  margin: 0 auto;
  min-height: 100vh;
}

.header {
  display: flex;
  align-items: center;
  padding: 12px;
  position: sticky;
  top: 0;
  background-color: #000;
  border-radius: 8px;
  z-index: 10;
  border-bottom: 1px solid #333;
}

.header h1 {
  margin: 0;
  color: #3aa757;
  font-size: 18px;
  flex: 1;
  text-align: center;
}

/* Search Section Styles */
.search-section {
  padding: 12px;
  border-radius: 8px;
  background-color: #111;
  border-bottom: 1px solid #333;
}

.search-container {
  position: relative;
}

.search-input-group {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  padding: 10px 12px;
  padding-right: 40px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  color: #e2e2e2;
  font-size: 14px;
  outline: none;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  border-color: #3aa757;
}

.search-input::placeholder {
  color: #666;
}

.clear-search-btn {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
  border-radius: 50%;
  transition: background-color 0.3s ease;
}

.clear-search-btn:hover {
  background-color: #333;
}

/* Filter Section Styles */
.filter-section {
  padding: 8px 12px;
  border-radius: 8px;
  background-color: #0a0a0a;
  border-bottom: 1px solid #333;
}

.filter-container {
  width: 100%;
}

.filter-tabs {
  display: flex;
  gap: 4px;
  width: 100%;
  justify-content: space-between;
}

.filter-tab {
  padding: 4px 8px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 12px;
  color: #999;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  text-align: center;
}

.filter-tab:hover {
  background-color: #2a2a2a;
  border-color: #444;
}

.filter-tab.active {
  background-color: #3aa757;
  border-color: #3aa757;
  color: #e2e2e2;
}

/* Count Badge Styles */
.count-badge {
  background-color: #333;
  color: #e2e2e2;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 4px;
  border-radius: 8px;
  min-width: 14px;
  text-align: center;
  line-height: 1.2;
  transition: all 0.3s ease;
}

.filter-tab.active .count-badge {
  background-color: rgba(255, 255, 255, 0.2);
  color: #e2e2e2;
}

.filter-tab:hover .count-badge {
  background-color: #444;
}

.filter-tab.active:hover .count-badge {
  background-color: rgba(255, 255, 255, 0.3);
}

.orders-content {
  padding: 12px;
}

.loading, .no-orders, .no-results {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

.no-results {
  color: #f39c12;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.order-card {
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border-left: 3px solid #333;
}

.order-card.expanded {
  border-color: #3aa757;
}

/* Order Card Status Indicators */
.order-card:has(.status-pending) {
  border-left-color: #ffd43b;
}

.order-card:has(.status-acknowledged) {
  border-left-color: #74c0fc;
}

.order-card:has(.status-confirmed) {
  border-left-color: #51cf66;
}

.order-card:has(.status-cancelled) {
  border-left-color: #ff6b6b;
}

.order-card:has(.status-delivered) {
  border-left-color: #868e96;
}

.order-summary {
  padding: 12px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.order-info .order-id {
  font-weight: bold;
  color: #3aa757;
  font-size: 14px;
}

.order-info .order-date {
  color: #999;
  font-size: 11px;
  margin-top: 2px;
}

.order-info .payment-status {
  background-color: #333;
  color: #e2e2e2;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
  margin-top: 3px;
  display: inline-block;
}

.order-totals {
  text-align: right;
}

.order-totals .total-items {
  color: #999;
  font-size: 11px;
}

.order-totals .total-amount {
  font-weight: bold;
  color: #3aa757;
  font-size: 14px;
}

.customer-info {
  margin-bottom: 8px;
  padding: 6px 0;
  border-top: 1px solid #333;
  border-bottom: 1px solid #333;
}

.customer-info .mobile {
  font-weight: 500;
  margin-bottom: 3px;
  font-size: 14px;
}

.order-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 11px;
}

.payment-method {
  color: #999;
}

/* Order Status Colors - Dark Theme Adapted */
.order-status {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  transition: all 0.3s ease;
}

.status-pending {
  background-color: #2d2006;
  color: #ffd43b;
  border: 1px solid #ffd43b;
}

.status-acknowledged {
  background-color: #0c2947;
  color: #74c0fc;
  border: 1px solid #74c0fc;
}

.status-confirmed {
  background-color: #0a3818;
  color: #51cf66;
  border: 1px solid #51cf66;
}

.status-cancelled {
  background-color: #2b0e0e;
  color: #ff6b6b;
  border: 1px solid #ff6b6b;
}

.status-delivered {
  background-color: #212529;
  color: #868e96;
  border: 1px solid #868e96;
}

.status-default {
  background-color: #333;
  color: #999;
  border: 1px solid #555;
}

.see-details-btn {
  width: 100%;
  background-color: #3aa757;
  color: #e2e2e2;
  border: none;
  border-radius: 6px;
  padding: 8px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
}

.see-details-btn:hover {
  background-color: #2e8a47;
}

.order-details {
  border-top: 1px solid #333;
  padding: 12px;
  background-color: #111;
}

.details-header {
  margin-bottom: 12px;
}

.details-header h3 {
  margin: 0 0 6px 0;
  color: #3aa757;
  font-size: 14px;
}

.note-section{
  /* margin: 0 0 6px 0; */
  color: #e2e2e2;
  font-size: 11px;
}

.items-summary {
  color: #999;
  font-size: 11px;
}

.items-list {
  margin-bottom: 16px;
}

.item-card {
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 4px;
  padding: 6px;
  margin-bottom: 6px;
  display: block; /* Changed from flex to block */
  transition: all 0.3s ease;
}

.item-card .item-info {
  width: 100%;
  margin-bottom: 4px;
}

.item-card.acknowledged {
  background-color: #222;
  border: 1px solid #444;
  opacity: 0.9;
}

.item-info {
  flex: 1;
}

.medicine-name {
  font-weight: 500;
  margin-bottom: 2px;
  color: #e2e2e2;
  font-size: 14px;
}

.medicine-category {
  color: #999;
  font-size: 11px;
  margin-bottom: 4px;
}

.item-details {
  display: flex;
  justify-content: space-between; /* Changed to space-between */
  align-items: center;
  font-size: 11px;
  flex-wrap: nowrap; /* Prevent wrapping */
  line-height: 1.1;
}
.item-details .quantity {
  color: #3aa757;
  flex: 1;
}

.item-details .amount {
  color: #e2e2e2;
  font-weight: 500;
  text-align: right;
  margin-left: 6px;
}

.quantity {
  color: #3aa757;
}

.bonus-qty {
  color: #ff6b6b;
  margin-left: 6px;
  font-size: 11px;
}
.amount {
  color: #e2e2e2;
  font-weight: 500;
}

.availability-status {
  margin-top: 4px;
  display: flex;
  justify-content: flex-end; /* Align to right */
}

.availability-indicator {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.availability-indicator.available {
  background-color: #3aa757;
  color: #e2e2e2;
}

.availability-indicator.not-available {
  background-color: #e74c3c;
  color: #e2e2e2;
}

.availability-indicator.pending {
  background-color: #f39c12;
  color: #000;
}

.action-buttons {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid #333;
  margin-bottom: 12px;
}

.cancel-btn, .confirm-btn {
  flex: 1;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn {
  background-color: #e74c3c;
  color: #e2e2e2;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #c0392b;
  transform: translateY(-1px);
}

.confirm-btn {
  background-color: #3aa757;
  color: #e2e2e2;
}

.confirm-btn:hover:not(:disabled) {
  background-color: #2e8a47;
  transform: translateY(-1px);
}

.cancel-btn:disabled, .confirm-btn:disabled {
  background-color: #333;
  cursor: not-allowed;
  opacity: 0.7;
  color: #666;
}

.status-section {
  border-top: 1px solid #333;
  padding-top: 8px;
}

.status-message {
  width: 90%;
  font-size: 11px;
  padding: 8px 12px;
  border-radius: 6px;
  background-color: #1a1a1a;
  border: 1px solid #333;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.status-message.status-pending {
  background-color: #2d2006;
  border-color: #ffd43b;
  color: #ffd43b;
}

.status-message.status-acknowledged {
  background-color: #0c2947;
  border-color: #74c0fc;
  color: #74c0fc;
}

.status-message.status-confirmed {
  background-color: #0a3818;
  border-color: #51cf66;
  color: #51cf66;
}

.status-message.status-cancelled {
  background-color: #2b0e0e;
  border-color: #ff6b6b;
  color: #ff6b6b;
}

.status-message.status-delivered {
  background-color: #212529;
  border-color: #868e96;
  color: #868e96;
}

/* Toast Message Styles */
.toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: #333;
  color: #e2e2e2;
  padding: 12px 20px;
  border-radius: 6px;
  font-size: 14px;
  z-index: 1000;
  max-width: 90%;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease-out;
}

.toast.success {
  background-color: #3aa757;
}

.toast.error {
  background-color: #e74c3c;
}

.toast.info {
  background-color: #3498db;
}

@keyframes slideUp {
  from {
    transform: translateX(-50%) translateY(100px);
    opacity: 0;
  }
  to {
    transform: translateX(-50%) translateY(0);
    opacity: 1;
  }
}

/* Order Summary Styles - Simplified */

.order-summary-section {
  padding: 12px;
  background-color: #1a1a1a;
  border-radius: 6px;
  border: 1px solid #333;
}

.summary-details {
  margin-bottom: 12px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #333;
  font-size: 11px;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  font-weight: 500;
  color: #999;
}

.summary-value {
  font-weight: 600;
  color: #e2e2e2;
}

.discount-amount {
  color: #ff6b6b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.total-row {
  border-top: 1px solid #3aa757;
  margin-top: 6px;
  padding-top: 8px;
}

.total-amount {
  font-size: 14px;
  color: #3aa757;
  font-weight: bold;
}


/* Responsive adjustments for very small screens */
@media (max-width: 360px) {
  .filter-tab {
    font-size: 11px;
    padding: 5px 12px;
  }
  
  .count-badge {
    font-size: 11px;
    padding: 1px 4px;
  }
  
  .order-header {
    flex-direction: column;
    gap: 8px;
  }
  
  .order-totals {
    text-align: left;
  }
  
  /* .item-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  } */

  /* .item-details .amount {
    align-self: flex-end;
    margin-left: 0;
  } */
  
  .availability-status {
    justify-content: flex-start;
  }
  
  .action-buttons {
    flex-direction: column;
  }
}

/* Scrollbar styling removed - no longer needed */

/* Loading animation */
.loading {
  position: relative;
}

.loading::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border: 2px solid #333;
  border-top: 2px solid #3aa757;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Hover effects for better UX */
.order-card:hover {
  background-color: #222;
  border-color: #444;
}

.item-card:hover {
  background-color: #222;
  border-color: #444;
}

/* .item-card:has(.availability-indicator.partial) {
  border-color: #ffd43b;
  border-width: 1px;
}

.item-card:has(.availability-indicator.not-available) {
  border-color: #ff6b6b;
  border-width: 1px;
}
.item-card:has(.availability-indicator.available) {
  border-color: #51cf66;
  border-width: 1px;
} */

/* Focus styles for accessibility */
.search-input:focus,
.filter-tab:focus,
.see-details-btn:focus,
.cancel-btn:focus,
.confirm-btn:focus {
  outline: 2px solid #3aa757;
  outline-offset: 2px;
}

/* Smooth transitions */
* {
  box-sizing: border-box;
}

.order-card,
.item-card,
.filter-tab,
button {
  transition: all 0.3s ease;
}
</style>